import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Sparkles, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";

export default function AccessCodeSection() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    const cleaned = (code || "").trim().toUpperCase();
    if (!cleaned) {
      toast.error("Please enter your access code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/access/validate-code", { code: cleaned });
      setValidated(data);
      toast.success("Code accepted — complimentary access unlocked.");
      // Route to signup with code pre-filled and invite tab pre-selected
      setTimeout(() => navigate(`/signup?code=${encodeURIComponent(cleaned)}`), 600);
    } catch (err) {
      const detail = err.response?.data?.detail || "Invalid code.";
      toast.error(detail);
      setValidated(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border-b border-stone-200 bg-white">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1">
              <Sparkles className="h-3 w-3 text-emerald-700" />
              <span className="label-mono !text-[10px] !text-emerald-800">Complimentary Access</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl">
              Have an access code?<br />
              <span className="text-stone-400">Skip the checkout.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-stone-700">
              If your program, chamber, university, or partner has issued you an ATLAS™ access code,
              enter it here to unlock the entire platform — free. No credit card required.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-stone-700">
              {[
                "Full Business DNA™ onboarding",
                "Unlimited Business Einstein™ conversations",
                "All AI modules & document exports",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} className="rounded-lg border border-stone-200 bg-stone-50 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-950 text-white">
                <KeyRound className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="label-mono">Access Code</div>
                <div className="mt-0.5 font-display text-lg font-medium text-slate-950">Enter to unlock</div>
              </div>
            </div>
            <label className="mt-6 block">
              <span className="label-mono block mb-2">Your code</span>
              <input
                data-testid="access-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
                placeholder="e.g. RIBBA1"
                className="w-full rounded-md border border-stone-300 bg-white px-3 py-3 font-mono text-base uppercase tracking-[0.15em] text-slate-950 outline-none transition-all focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                autoComplete="off"
              />
            </label>
            <button
              type="submit"
              data-testid="access-code-submit"
              disabled={loading}
              className="btn-primary mt-4 w-full"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</>
                       : <>Redeem Code <ArrowUpRight className="h-4 w-4" /></>}
            </button>
            {validated?.valid && (
              <div className="mt-4 flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="text-emerald-800">{validated.message} Redirecting to sign up…</span>
              </div>
            )}
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-wider text-stone-500">
              No card required • Instant activation
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
